import { motionReduced } from "./motion.js";

// One external-link mark per shelf, because each tile's own mark popping in and out was
// jarring. It shows up on the first tile your pointer or keyboard focus enters. While the
// pointer stays in the shelf, it travels to the next tile's corner like a drop of ink,
// stretching out as it speeds up and rounding off as it lands. Its arrow loops when it first
// shows up (`external-escape` in site.scss). After a move, the arrow waits until the mark has
// sat still for 150ms, so it doesn't play mid-flight.
//
// With reduced motion it jumps straight there, and the arrow stays put. Without the script,
// each tile shows its own mark.
//
//   tile ──pointerenter──▶ target corner ──spring──▶ mark (rAF, transform only)
//   mark at rest ──150ms──▶ arrow loop (another move first starts the wait over)
//   shelf ──pointerleave──▶ 80ms ──▶ hidden

// The mark's spring: stiffness, damping ratio, and how far its speed stretches it.
const STIFFNESS = 220;
const DAMPING = 0.62;
const STRETCH_SPEED = 1400;
const STRETCH_MAX = 0.55;
// The mark's inset from its tile's top right corner, and its size, in CSS pixels
// (_about.scss).
const INSET = 6;
const SIZE = 24;
// How long the pointer can be outside the shelf before the mark goes away. The labels'
// background waits just as long (`external-label` in _tokens.scss).
const LEAVE_MS = 80;
// How long the mark sits still after a move before its arrow loops.
const REST_MS = 150;
// The longest frame the spring takes in one step, in seconds.
const STEP_MAX = 0.032;

export function startShelfMark(shelf: HTMLElement): void {
  const tiles = [...shelf.querySelectorAll("a")];
  const own = shelf.querySelector(".tile-external");
  const first = tiles[0]?.parentElement;
  const host = first?.offsetParent;
  if (!own || !(host instanceof HTMLElement)) return;
  const clone = own.cloneNode(true);
  if (!(clone instanceof SVGElement)) return;
  const mark: SVGElement = clone;
  mark.classList.add("shelf-mark");
  // The text effects ignore it, fades included (text-effects.ts). It hides along with its
  // tile's picture, not based on where it is.
  mark.dataset.fx = "none";
  host.append(mark);
  shelf.toggleAttribute("data-mark", true);

  let current: HTMLAnchorElement | null = null;
  let shown = false;
  let x = 0;
  let y = 0;
  let vx = 0;
  let vy = 0;
  let tx = 0;
  let ty = 0;
  let frame = 0;
  let last = 0;
  let leaving = 0;
  let resting = 0;

  // The mark copies its tile's accent, which a wave repaints (accents.ts).
  const accent = new MutationObserver(() => copyAccent());
  function copyAccent(): void {
    const value = current?.style.getPropertyValue("--accent") ?? "";
    if (value) mark.style.setProperty("--accent", value);
    else mark.style.removeProperty("--accent");
  }

  function place(stretch = 0, angle = 0): void {
    const across = 1 / (1 + stretch * 0.8);
    mark.style.transform =
      `translate(${x}px, ${y}px) rotate(${angle}rad) ` +
      `scale(${1 + stretch}, ${across}) rotate(${-angle}rad)`;
  }

  function loop(): void {
    if (!motionReduced()) mark.toggleAttribute("data-looping", true);
  }
  mark.addEventListener("animationend", () => mark.removeAttribute("data-looping"));
  mark.addEventListener("animationcancel", () => mark.removeAttribute("data-looping"));

  function step(now: number): void {
    const dt = Math.min(STEP_MAX, (now - last) / 1000);
    last = now;
    const damping = 2 * DAMPING * Math.sqrt(STIFFNESS);
    vx += (STIFFNESS * (tx - x) - damping * vx) * dt;
    vy += (STIFFNESS * (ty - y) - damping * vy) * dt;
    x += vx * dt;
    y += vy * dt;
    const speed = Math.hypot(vx, vy);
    if (Math.hypot(tx - x, ty - y) < 0.3 && speed < 4) {
      x = tx;
      y = ty;
      vx = 0;
      vy = 0;
      place();
      frame = 0;
      resting = window.setTimeout(loop, REST_MS);
      return;
    }
    place(Math.min(STRETCH_MAX, speed / STRETCH_SPEED), Math.atan2(vy, vx));
    frame = requestAnimationFrame(step);
  }

  function go(tile: HTMLAnchorElement): void {
    window.clearTimeout(leaving);
    window.clearTimeout(resting);
    const item = tile.parentElement;
    if (!item || tile.querySelector("img[data-halftone]")) return;
    tx = item.offsetLeft + item.offsetWidth - INSET - SIZE;
    ty = item.offsetTop + INSET;
    if (current !== tile) {
      accent.disconnect();
      current = tile;
      accent.observe(tile, { attributes: true, attributeFilter: ["style"] });
      copyAccent();
    }
    if (!shown || motionReduced()) {
      // The first tile: the mark just appears there, the way a tile's own mark did.
      cancelAnimationFrame(frame);
      frame = 0;
      x = tx;
      y = ty;
      vx = 0;
      vy = 0;
      place();
      if (!shown) loop();
      shown = true;
      mark.toggleAttribute("data-shown", true);
      return;
    }
    if (!frame) {
      last = performance.now();
      frame = requestAnimationFrame(step);
    }
  }

  function hide(): void {
    window.clearTimeout(leaving);
    window.clearTimeout(resting);
    leaving = window.setTimeout(() => {
      shown = false;
      mark.removeAttribute("data-shown");
    }, LEAVE_MS);
  }

  for (const tile of tiles) {
    tile.addEventListener("pointerenter", () => go(tile));
    tile.addEventListener("focus", () => {
      if (tile.matches(":focus-visible")) go(tile);
    });
    tile.addEventListener("blur", () => {
      if (!shelf.matches(":hover")) hide();
    });
  }
  shelf.addEventListener("pointerleave", hide);
  // A tile dissolving under the face ignores the pointer (_about.scss), so the mark goes
  // away with it.
  window.addEventListener(
    "scroll",
    () => {
      if (shown && current?.querySelector("img[data-halftone]")) hide();
    },
    { passive: true },
  );
}
